import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/v1';

/** Základ API URL – potřebuje ho i uvolnění editačního zámku přes fetch(keepalive). */
export const API_BASE_URL = BASE_URL;

export const apiClient = axios.create({
  baseURL:        BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ---------- Request interceptor – přidá JWT token, opraví Content-Type pro FormData ----------
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Přidej X-Branch-Id header pokud admin přepnul pobočku (multi-branch addon)
  const activeBranchId = sessionStorage.getItem('active_branch_id');
  if (activeBranchId) {
    config.headers['X-Branch-Id'] = activeBranchId;
  }

  // Axios neumí přepsat výchozí Content-Type pro FormData, pokud je nastavený globálně.
  // Smažeme ho ručně – axios pak nastaví multipart/form-data včetně boundary automaticky.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// ---------- Response interceptor – refresh token při 401 ----------
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: string) => void; reject: (e: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) { prom.reject(error); }
    else        { prom.resolve(token!); }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Demo režim – server zablokoval zápisovou operaci
    if (error.response?.status === 403 && error.response?.data?.code === 'DEMO_MODE_READONLY') {
      window.alert(error.response.data.message ?? 'Toto je pouze ukázková verze – úpravy nejsou povoleny.');
      return Promise.reject(error);
    }

    // Editační zámek – záznam právě upravuje někdo jiný (viz hooks/useEditLock.ts).
    // Záchytná síť pro všechna místa, kde zámek neřeší přímo UI.
    if (error.response?.status === 423) {
      window.alert(error.response.data?.message ?? 'Záznam právě upravuje jiný uživatel. Zkuste to za chvíli.');
      return Promise.reject(error);
    }

    // Obchodní podmínky – majitel je ještě neodsouhlasil, API blokuje zbytek
    // systému (viz api/modules/terms). Dáme vědět TermsGate, ať si stav znovu
    // načte a zobrazí okno se souhlasem; jinak by uživatel koukal na prázdné
    // stránky bez vysvětlení.
    if (error.response?.status === 451) {
      window.dispatchEvent(new CustomEvent('terms-required'));
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) throw new Error('Chybí obnovovací token');
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken }, { withCredentials: true });
        const newToken = data.data.accessToken;
        localStorage.setItem('access_token', newToken);
        if (data.data.refreshToken) localStorage.setItem('refresh_token', data.data.refreshToken);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
